import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

// Interfaces/Types
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { IAccessPositions, ISelected, IWashoutQueries } from "@/Washouts/types/WashoutReview";

// API's
import { getMappedPosition } from "@/Common/api/positions/queries";

// Hooks
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import useWSSubscriptions from "@/Washouts/hooks/common/useWSSubscriptions";
import { useSingularPosition } from "@/Common/hooks/Positions";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Helpers
import { resolveCheck, resolveEmptyText, resolveImage, resolveLoading } from "@/Washouts/helpers/WashoutReview";
import { isMutablePosition, normalizePositions } from "@/Common/helpers/Positions";
import { getPositionAccess } from "@/Common/helpers/Positions/positionsAccess";
import { and } from "@/Common/helpers/sonar";

// Components
import EmptySection from "./EmptySection";
import { InfoSection } from "./InfoSection";
import { LOADING_TIMEOUT } from "@/Washouts/constants/index";

type TContainerProps = {
	locationId: string;
	selected: ISelected | null;
	currentCommissionCycleId: string;
	currentPayPeriod?: IPayPeriod;
	accessPositions: IAccessPositions;
};

const QUERY_IDENTIFIERS = ["getWashout"];

const Container = (props: TContainerProps) => {
	const { locationId, selected, currentCommissionCycleId, currentPayPeriod, accessPositions } = props;
	const { userId, position, payPeriodId, commissionCycleId } = selected ?? { userId: "", position: "", payPeriodId: "" };
	const user = useSelector((state: RootState) => state.user.user);
	const positionToUse = useSingularPosition(position);
	const locationPath = useLocation();
	const { useAuth } = useAuthorization();
	const positionAccess = getPositionAccess(positionToUse);
	const POSITION_OVERRIDE_ACCESS = positionAccess?.washouts?.override ?? "";
	const overrideCompensation = useAuth(POSITION_OVERRIDE_ACCESS);
	const { useWashoutQueries } = useFactoryWashout();
	const selectedPosition = normalizePositions(position || locationPath?.state?.position);
	const washoutQueries = useWashoutQueries(selectedPosition) as unknown as IWashoutQueries;
	const { clearCache } = useCacheCleaner();

	const [{ data: washoutData, isLoading: isLoadingWashout }, { data: positionType, isLoading: isLoadingPositionType }] = useQueries({
		queries: [
			{
				queryKey: ["getWashout", payPeriodId, position, userId, commissionCycleId],
				queryFn: async () => washoutQueries?.getWashout?.(locationId, userId, commissionCycleId ?? "", payPeriodId, position),
				enabled: and(!!locationId, !!selected?.userId, !!commissionCycleId),
				staleTime: Infinity,
			},
			{
				queryKey: ["positionType", locationId, selectedPosition],
				queryFn: async () => {
					const isMutable = isMutablePosition(selectedPosition);
					if (isMutable) {
						const mappedPosition = await getMappedPosition(locationId, selectedPosition);
						return mappedPosition?.isDepartmental ? "Departmental" : "Producer";
					}
					return "Producer";
				},
				enabled: and(!!locationId, !!selectedPosition),
				staleTime: Infinity,
			},
		],
	});

	const [isLoadingSummary, setIsLoadingSummary] = useState(false);

	const { isEditableWashout, isApproved } = resolveCheck(washoutData, currentPayPeriod);
	const isLoading = resolveLoading(isLoadingWashout, isLoadingPositionType, isLoadingSummary);

	useWSSubscriptions({
		commissionCycleId: commissionCycleId ?? "",
		payPeriodId: payPeriodId ?? "",
		userId: userId ?? "",
		position: position ?? "",
		dependencies: [selected],
		onCreateCallback: () => setIsLoadingSummary(false),
	});

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	const handleRecalculateWashout = () => {
		setIsLoadingSummary(true);
		setTimeout(() => setIsLoadingSummary(false), LOADING_TIMEOUT);
	};

	return (
		<>
			<InfoSection
				selected={selected}
				washoutData={washoutData}
				isLoading={isLoading}
				positionToUse={positionToUse}
				positionType={positionType}
				overrideCompensation={overrideCompensation}
				isEditableWashout={isEditableWashout}
				isApproved={isApproved}
				locationId={locationId}
				washoutQueries={washoutQueries}
				user={user}
				accessPositions={accessPositions}
				currentCommissionCycleId={currentCommissionCycleId}
				onRecalculateWashout={handleRecalculateWashout}
			/>
			{!isLoading && <EmptySection image={resolveImage(selected)} washoutData={washoutData} title={resolveEmptyText(selected)} />}
		</>
	);
};

export default Container;
